// src/pages/customer/services/revenues/importCustomerExcel/index.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Download, FileSpreadsheet, X } from "lucide-react";
import { useHeaderAction } from "../../../../../core/provider/HeaderActionProvider/HeaderAction.js";
import HeaderSubPages from "../../../../../components/ui/HeaderSubPages/index.js";
import { Button } from "../../../../../components/ui/Button/index.js";
import Modal from "../../../../../components/ui/ModalAdmin/index.js";
import AdminSelectCustomerList from "../../../../../pages/admin/customers/components/selectCustomerList/index.js"; // مسیرت رو تنظیم کن
import { toastify } from "../../../../../components/ui/Toasts/index.js";
import { useImportCustomerWithExcel } from "../../../../../core/api/customer/revenues/mutation.js";
import SelectPlanModal from "./components/index.js";

const SAMPLE_FILE = "/app/templates/test1.xlsx";
const SAMPLE_DOWNLOAD_NAME = "نمونه-فایل-افتتاح-حساب.xlsx";

const CustomerImportCustomerExcelPage = () => {
    const navigate = useNavigate();
    const { setAction } = useHeaderAction();

    const { mutate: importExcel, isPending } = useImportCustomerWithExcel();

    const [isOpen, setIsOpen] = useState(false);
    const [file, setFile] = useState<any>(null);
    const [error, setError] = useState("");

    const [plan1, setPlan1] = useState<any>(null);
    const [plan2, setPlan2] = useState<any>(null);
    const [plan3, setPlan3] = useState<any>(null);

    const [isPlan1Open, setIsPlan1Open] = useState(false);
    const [isPlan2Open, setIsPlan2Open] = useState(false);
    const [isPlan3Open, setIsPlan3Open] = useState(false);

    // ===== هدر =====
    useEffect(() => {
        setAction(
            <HeaderSubPages
                title="ورود اکسل افتتاح حساب"
                onBack={() => navigate(-1)}
            />
        );
        return () => setAction(null);
    }, [setAction, navigate]);

    // ===== دانلود نمونه =====
    const handleDownloadSample = () => {
        try {
            const link = document.createElement("a");
            link.href = SAMPLE_FILE;
            link.download = SAMPLE_DOWNLOAD_NAME;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toastify("success", "فایل نمونه در حال دانلود است");
        } catch {
            toastify("error", "خطا در دانلود فایل نمونه");
        }
    };

    // ===== آپلود =====
    const handleUpload = () => {
        if (!plan1?.id) {
            toastify("warning", "انتخاب طرح اصلی الزامی است");
            return;
        }
        if (!file) {
            setError("لطفاً یک فایل انتخاب کنید");
            return;
        }

        importExcel(
            {
                file,
                streetPlanId1: Number(plan1.id),
                streetPlanId2: plan2?.id ? Number(plan2.id) : undefined,
                streetPlanId3: plan3?.id ? Number(plan3.id) : undefined,
            },
            {
                onSuccess: (blob: Blob) => {
                    try {
                        const url = window.URL.createObjectURL(
                            new Blob([blob], {
                                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                            })
                        );
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = "result.xlsx";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                    } catch {}

                    toastify("success", "فایل با موفقیت پردازش شد");
                    setIsOpen(false);
                    setFile(null);
                    setPlan1(null);
                    setPlan2(null);
                    setPlan3(null);
                },
                onError: async (err: any) => {
                    let message = "خطا در آپلود فایل";
                    if (err?.response?.data instanceof Blob) {
                        try {
                            const text = await err.response.data.text();
                            const json = JSON.parse(text);
                            message = json?.message || message;
                        } catch {}
                    }
                    setError(message);
                    toastify("error", message);
                },
            }
        );
    };

    // ===== انتخاب‌گر طرح =====
    const PlanPickerButton = ({
        label,
        plan,
        onClear,
        onOpen,
        required,
    }: any) => (
        <div className="relative w-full">
            <button
                type="button"
                onClick={onOpen}
                className="flex h-12 w-full items-center justify-between rounded-xl border border-neutral-200 bg-white px-3 text-sm"
            >
                <span className={plan ? "text-neutral-800" : "text-neutral-400"}>
                    {plan?.name || label}
                </span>
                <FileSpreadsheet className="h-4 w-4 text-neutral-400" />
            </button>
            {plan && !required && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white p-1 text-red-500"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );

    return (
        <div className="flex flex-col h-full -m-3">
            <div className="flex-1 min-h-0 p-3 overflow-auto space-y-3">
                <div className="bg-white border rounded-xl border-neutral-100 p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50">
                            <FileSpreadsheet className="h-4 w-4 text-brand-600" />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-neutral-800">
                                ورود اکسل افتتاح حساب
                            </p>
                            <p className="text-[11px] text-neutral-500">
                                طرح اصلی الزامی است، طرح‌های رزرو اختیاری.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1  gap-3">
                        <PlanPickerButton
                            label="انتخاب طرح اصلی *"
                            plan={plan1}
                            onOpen={() => setIsPlan1Open(true)}
                            required
                        />
                        <PlanPickerButton
                            label="طرح رزرو ۱ (اختیاری)"
                            plan={plan2}
                            onOpen={() => setIsPlan2Open(true)}
                            onClear={() => setPlan2(null)}
                        />
                        <PlanPickerButton
                            label="طرح رزرو ۲ (اختیاری)"
                            plan={plan3}
                            onOpen={() => setIsPlan3Open(true)}
                            onClear={() => setPlan3(null)}
                        />
                    </div>
                </div>

                <div className="bg-white border rounded-xl border-neutral-100 p-4">
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            rounded="lg"
                            color="neutral"
                            className="!h-9"
                            leftIcon={<Download className="w-3.5 h-3.5" />}
                            onClick={handleDownloadSample}
                        >
                            دانلود نمونه
                        </Button>
                        <Button
                            variant="solid"
                            size="sm"
                            rounded="lg"
                            color="primary"
                            className="!h-9"
                            leftIcon={<Upload className="w-3.5 h-3.5" />}
                            onClick={() => {
                                setError("");
                                setFile(null);
                                setIsOpen(true);
                            }}
                        >
                            آپلود فایل
                        </Button>
                    </div>
                </div>
            </div>

            {/* ===== مودال آپلود ===== */}
            <Modal
                isOpen={isOpen}
                title="آپلود فایل افتتاح حساب"
                onClose={() => setIsOpen(false)}
                closeOnOverlayClick={!isPending}
                showCloseButton={!isPending}
                size="md"
                animation="fade"
                footer={
                    <div className="flex items-center w-full gap-2">
                        <Button
                            onClick={() => setIsOpen(false)}
                            variant="outline"
                            size="sm"
                            rounded="lg"
                            color="neutral"
                            className="w-1/2 !h-9"
                            disabled={isPending}
                        >
                            انصراف
                        </Button>
                        <Button
                            variant="solid"
                            size="sm"
                            rounded="lg"
                            color="primary"
                            className="w-1/2 !h-9"
                            onClick={handleUpload}
                            loading={isPending}
                            disabled={isPending || !file}
                        >
                            {isPending ? "در حال آپلود..." : "تایید و آپلود"}
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-3 pb-2 pt-2">
                    <AdminSelectCustomerList
                        onSelectFile={(value) => {
                            setFile(value as any);
                            setError("");
                        }}
                    />
                    {error && <p className="text-[11px] text-red-600">{error}</p>}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <p className="text-[11px] text-blue-700">
                            <span className="font-semibold">راهنما:</span> فایل باید با فرمت Excel (.xlsx) باشد.
                        </p>
                    </div>
                </div>
            </Modal>

            {/* ===== انتخاب‌گرهای طرح ===== */}
            <SelectPlanModal
                title="انتخاب طرح اصلی"
                des="طرح اصلی را انتخاب کنید"
                isOpen={isPlan1Open}
                onClose={() => setIsPlan1Open(false)}
                selectedPlanId={plan1?.id ?? null}
                selectedPlan={plan1}
                onSelectPlan={(p: any) => {
                    setPlan1(p);
                    setIsPlan1Open(false);
                }}
            />
            <SelectPlanModal
                title="انتخاب طرح رزرو ۱"
                des="طرح رزرو ۱ را انتخاب کنید"
                isOpen={isPlan2Open}
                onClose={() => setIsPlan2Open(false)}
                selectedPlanId={plan2?.id ?? null}
                selectedPlan={plan2}
                onSelectPlan={(p: any) => {
                    setPlan2(p);
                    setIsPlan2Open(false);
                }}
            />
            <SelectPlanModal
                title="انتخاب طرح رزرو ۲"
                des="طرح رزرو ۲ را انتخاب کنید"
                isOpen={isPlan3Open}
                onClose={() => setIsPlan3Open(false)}
                selectedPlanId={plan3?.id ?? null}
                selectedPlan={plan3}
                onSelectPlan={(p: any) => {
                    setPlan3(p);
                    setIsPlan3Open(false);
                }}
            />
        </div>
    );
};

export default CustomerImportCustomerExcelPage;